<template>
  <div class="step-address-container">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linejoin="round"
          />

          <circle
            cx="12"
            cy="9"
            r="2.5"
            stroke="currentColor"
            stroke-width="1.8"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Место работы
        </span>

        <h3 class="step-heading-title">
          Адрес вакансии
        </h3>
      </div>

      <Transition name="status">
        <div
          v-if="isAddressValid"
          class="header-status"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              fill="currentColor"
            />

            <path
              d="m6.4 10.1 2.2 2.2 5-5"
              stroke="white"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          Адрес указан
        </div>
      </Transition>
    </div>

    <!-- Описание -->
    <p class="step-description">
      Укажите место, где будет работать сотрудник. По умолчанию мы
      используем адрес компании, но его можно изменить для этой вакансии.
    </p>

    <!-- Выбор режима -->
    <label
      class="manual-card"
      :class="{ active: isManual }"
    >
      <div class="manual-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 20h4l10.7-10.7a2.1 2.1 0 0 0-3-3L5 17v3Z"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linejoin="round"
          />

          <path
            d="m14.5 7.5 3 3"
            stroke="currentColor"
            stroke-width="1.7"
          />
        </svg>
      </div>

      <div class="manual-content">
        <span class="manual-title">
          Указать другой адрес
        </span>

        <span class="manual-description">
          Использовать адрес, отличный от адреса компании
        </span>
      </div>

      <input
        v-model="isManual"
        type="checkbox"
        class="toggle-input"
        @change="handleManualChange"
      />

      <span class="toggle-slider">
        <span class="toggle-thumb"></span>
      </span>
    </label>

    <!-- Адрес -->
    <div class="field-container">
      <div class="field-heading">
        <div>
          <label
            class="field-label"
            for="vacancy-address"
          >
            Адрес места работы
          </label>

          <span class="field-subtitle">
            {{
              isManual
                ? "Введите адрес вручную"
                : "Адрес загружен из компании"
            }}
          </span>
        </div>

        <Transition name="status" mode="out-in">
          <span
            v-if="isSearchingAddress"
            key="search"
            class="address-status searching"
          >
            <span class="small-loader"></span>
            Ищем
          </span>

          <span
            v-else-if="hasCoordinates"
            key="coordinates"
            class="address-status confirmed"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="10"
                cy="10"
                r="8"
                fill="currentColor"
              />

              <path
                d="m6.4 10.1 2.2 2.2 5-5"
                stroke="white"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            Найден
          </span>
        </Transition>
      </div>

      <div
        class="address-input-shell"
        :class="{
          disabled: !isManual,
          focused: isAddressFocused,
          valid: isAddressValid,
          error: showAddressError
        }"
      >
        <div class="address-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 21s7-5.25 7-12a7 7 0 1 0-14 0c0 6.75 7 12 7 12Z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round"
            />

            <circle
              cx="12"
              cy="9"
              r="2.5"
              stroke="currentColor"
              stroke-width="1.8"
            />
          </svg>
        </div>

        <input
          id="vacancy-address"
          v-model="address"
          type="text"
          class="address-input"
          :placeholder="
            isManual
              ? 'Например: Москва, ул. Тверская, 12'
              : 'Загружаем адрес компании'
          "
          :disabled="!isManual"
          autocomplete="street-address"
          @input="handleAddressInput"
          @focus="isAddressFocused = true"
          @blur="handleAddressBlur"
        />

        <Transition name="status">
          <div
            v-if="isAddressValid && !isSearchingAddress"
            class="valid-icon"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </Transition>
      </div>

      <Transition name="message" mode="out-in">
        <div
          v-if="showAddressError"
          key="error"
          class="field-message error-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M10 6.3v4.4M10 13.6v.1"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          {{ addressError }}
        </div>

        <div
          v-else
          key="hint"
          class="field-message hint-message"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              stroke="currentColor"
              stroke-width="1.5"
            />

            <path
              d="M10 9v5M10 6.2v.2"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          {{
            isManual
              ? "Укажите город, улицу и номер дома"
              : "Чтобы изменить адрес, включите ручной режим"
          }}
        </div>
      </Transition>
    </div>

    <!-- Карта -->
    <div class="map-section">
      <div class="map-heading">
        <div>
          <span class="map-title">
            Местоположение на карте
          </span>

          <span class="map-subtitle">
            Проверьте правильность адреса
          </span>
        </div>

        <span
          v-if="hasCoordinates"
          class="coordinates"
        >
          {{ formattedCoordinates }}
        </span>
      </div>

      <div
        class="map-wrapper"
        :class="{ loading: isMapLoading }"
      >
        <iframe
          :key="mapUrl"
          class="map-iframe"
          :src="mapUrl"
          title="Адрес вакансии"
          loading="lazy"
          allowfullscreen
          referrerpolicy="no-referrer-when-downgrade"
          @load="isMapLoading = false"
        ></iframe>

        <Transition name="map-loader">
          <div
            v-if="isMapLoading"
            class="map-loading"
          >
            <div class="map-loader-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>

            <span>Загружаем карту</span>
          </div>
        </Transition>

        <div class="map-gradient"></div>

        <Transition name="map-card">
          <div
            v-if="isAddressValid"
            class="map-address-card"
          >
            <div class="map-pin">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M10 18s5.5-4.2 5.5-9.5a5.5 5.5 0 1 0-11 0C4.5 13.8 10 18 10 18Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />

                <circle
                  cx="10"
                  cy="8.5"
                  r="1.8"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
              </svg>
            </div>

            <div class="map-address-content">
              <span>
                {{
                  isManual
                    ? "Адрес вакансии"
                    : "Адрес компании"
                }}
              </span>

              <strong>{{ address }}</strong>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Ошибка загрузки компании -->
    <Transition name="message">
      <div
        v-if="companyLoadError"
        class="company-error"
      >
        <span>{{ companyLoadError }}</span>

        <button
          type="button"
          @click="loadCompanyAddress"
        >
          Повторить
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch
} from "vue";

import CompanyService from "../../api/CompanyService";

const emit = defineEmits([
  "complete-step",
  "valid",
  "notValid"
]);

const GOOGLE_MAPS_API_KEY = "AIzaSyBSEN-wo__FMd5MeQUIkQYGFPuqZeHgVNY" || "";
const savedAddress =
  localStorage.getItem("vacancy_address") || "";

const savedManual =
  localStorage.getItem("vacancy_is_manual") === "true";

const address = ref(savedAddress);
const isManual = ref(savedManual);

const latitude = ref(
  parseStoredCoordinate("vacancy_latitude")
);

const longitude = ref(
  parseStoredCoordinate("vacancy_longitude")
);

const companyAddress = ref("");
const companyLatitude = ref(null);
const companyLongitude = ref(null);

const isAddressFocused = ref(false);
const isAddressTouched = ref(false);
const isSearchingAddress = ref(false);
const isMapLoading = ref(true);

const addressError = ref("");
const companyLoadError = ref("");

let geocodeTimer = null;
let geocodeController = null;
let geocodeSequence = 0;

const hasAddress = computed(() =>
  address.value.trim().length >= 3
);

const hasCoordinates = computed(() =>
  Number.isFinite(latitude.value) &&
  Number.isFinite(longitude.value)
);

const isAddressValid = computed(() =>
  hasAddress.value
);

const showAddressError = computed(() =>
  isAddressTouched.value &&
  !isSearchingAddress.value &&
  Boolean(addressError.value)
);

const addressToShow = computed(() =>
  address.value.trim() || "Москва"
);

const mapUrl = computed(() => {
  if (GOOGLE_MAPS_API_KEY) {
    const query = hasCoordinates.value
      ? `${latitude.value},${longitude.value}`
      : addressToShow.value;

    return (
      "https://www.google.com/maps/embed/v1/place" +
      `?key=${GOOGLE_MAPS_API_KEY}` +
      `&q=${encodeURIComponent(query)}` +
      "&zoom=15"
    );
  }

  /*
    Карта продолжит работать даже без API key,
    но ручное получение координат будет недоступно.
  */
  return (
    "https://www.google.com/maps" +
    `?q=${encodeURIComponent(addressToShow.value)}` +
    "&output=embed"
  );
});

const formattedCoordinates = computed(() => {
  if (!hasCoordinates.value) {
    return "";
  }

  return (
    `${latitude.value.toFixed(4)}, ` +
    longitude.value.toFixed(4)
  );
});

function parseStoredCoordinate(key) {
  const raw = localStorage.getItem(key);

  if (
    raw === null ||
    raw === "" ||
    raw === "null" ||
    raw === "undefined"
  ) {
    return null;
  }

  const value = Number.parseFloat(raw);

  return Number.isFinite(value)
    ? value
    : null;
}

function createManualAddressID() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `manual-${crypto.randomUUID()}`;
  }

  return (
    `manual-${Date.now()}-` +
    Math.random().toString(16).slice(2)
  );
}

function saveCoordinates() {
  if (hasCoordinates.value) {
    localStorage.setItem(
      "vacancy_latitude",
      String(latitude.value)
    );

    localStorage.setItem(
      "vacancy_longitude",
      String(longitude.value)
    );
  } else {
    localStorage.removeItem(
      "vacancy_latitude"
    );

    localStorage.removeItem(
      "vacancy_longitude"
    );
  }
}

function emitValidationState() {
  if (isAddressValid.value) {
    emit("valid");
  } else {
    emit("notValid");
  }
}

async function loadCompanyAddress() {
  if (isManual.value && address.value.trim()) {
    emitValidationState();
    return;
  }

  companyLoadError.value = "";

  const companyId =
    localStorage.getItem("vacancy_company_id") ||
    localStorage.getItem("company_id");

  if (!companyId) {
    companyLoadError.value =
      "Не удалось определить выбранную компанию";

    addressError.value =
      "Включите ручной режим и укажите адрес";

    emitValidationState();
    return;
  }

  try {
    console.log(
      "📦 Загружаем компанию:",
      companyId
    );

    const company =
      await CompanyService.fetchCompanyById(
        companyId
      );

    if (!company) {
      throw new Error("Компания не найдена");
    }

    const loadedAddress =
      company.street ||
      company.companyLocation?.address ||
      "";

    const loadedLatitude =
      Number(
        company.companyLocation?.latitude
      );

    const loadedLongitude =
      Number(
        company.companyLocation?.longitude
      );

    companyAddress.value = loadedAddress;

    companyLatitude.value =
      Number.isFinite(loadedLatitude)
        ? loadedLatitude
        : null;

    companyLongitude.value =
      Number.isFinite(loadedLongitude)
        ? loadedLongitude
        : null;

    if (!isManual.value) {
      address.value = companyAddress.value;
      latitude.value = companyLatitude.value;
      longitude.value = companyLongitude.value;

      localStorage.setItem(
        "vacancy_address",
        address.value
      );

      localStorage.setItem(
        "vacancy_is_manual",
        "false"
      );

      saveCoordinates();
    }

    if (company.regionId) {
      localStorage.setItem(
        "vacancy_city_id",
        String(company.regionId)
      );
    }

    const companyCity =
      company.city ||
      company.companyLocation?.city;

    if (companyCity) {
      localStorage.setItem(
        "vacancy_city",
        companyCity
      );
    }

    addressError.value = loadedAddress
      ? ""
      : "У компании не указан адрес";

    emitValidationState();
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки компании:",
      error
    );

    companyLoadError.value =
      "Не удалось загрузить адрес компании";

    addressError.value =
      "Включите ручной режим и укажите адрес";

    emitValidationState();
  }
}

function handleManualChange() {
  clearTimeout(geocodeTimer);
  geocodeController?.abort();

  addressError.value = "";
  isAddressTouched.value = false;

  if (isManual.value) {
    /*
      Оставляем адрес компании как начальное
      значение, но разрешаем его изменить.
    */
    localStorage.setItem(
      "vacancy_is_manual",
      "true"
    );

    emitValidationState();
    return;
  }

  address.value = companyAddress.value;
  latitude.value = companyLatitude.value;
  longitude.value = companyLongitude.value;

  localStorage.setItem(
    "vacancy_is_manual",
    "false"
  );

  localStorage.removeItem(
    "vacancy_address_id"
  );

  saveCoordinates();
  emitValidationState();
}

function handleAddressInput() {
  isAddressTouched.value = true;
  addressError.value = "";

  clearTimeout(geocodeTimer);
  geocodeController?.abort();

  latitude.value = null;
  longitude.value = null;

  saveCoordinates();
  emitValidationState();

  if (!hasAddress.value) {
    if (address.value.trim()) {
      addressError.value =
        "Введите более подробный адрес";
    }

    return;
  }

  geocodeTimer = window.setTimeout(() => {
    geocodeManualAddress();
  }, 650);
}

function handleAddressBlur() {
  isAddressFocused.value = false;
  isAddressTouched.value = true;

  if (!hasAddress.value) {
    addressError.value =
      "Укажите корректный адрес";
  }

  emitValidationState();
}

async function geocodeManualAddress() {
  if (
    !isManual.value ||
    !hasAddress.value
  ) {
    return false;
  }

  if (!GOOGLE_MAPS_API_KEY) {
    /*
      Адрес всё равно сохраняем, просто
      координаты получить не получится.
    */
    console.warn(
      "⚠️ VITE_GOOGLE_MAPS_API_KEY не настроен"
    );

    isMapLoading.value = true;
    return false;
  }

  const sequence = ++geocodeSequence;

  geocodeController?.abort();
  geocodeController =
    new AbortController();

  isSearchingAddress.value = true;
  addressError.value = "";

  const query = address.value.trim();

  const requestURL =
    "https://maps.googleapis.com/maps/api/geocode/json" +
    `?address=${encodeURIComponent(query)}` +
    `&key=${GOOGLE_MAPS_API_KEY}` +
    "&language=ru";

  try {
    const response = await fetch(
      requestURL,
      {
        signal: geocodeController.signal
      }
    );

    if (!response.ok) {
      throw new Error(
        `Google Maps HTTP ${response.status}`
      );
    }

    const data = await response.json();

    if (sequence !== geocodeSequence) {
      return false;
    }

    if (
      data.status === "OK" &&
      data.results?.length > 0
    ) {
      const result = data.results[0];
      const location =
        result.geometry?.location;

      if (
        !Number.isFinite(location?.lat) ||
        !Number.isFinite(location?.lng)
      ) {
        throw new Error(
          "Координаты не найдены"
        );
      }

      latitude.value = location.lat;
      longitude.value = location.lng;

      /*
        Используем аккуратно отформатированный
        адрес от Google.
      */
      if (result.formatted_address) {
        address.value =
          result.formatted_address;
      }

      const cityComponent =
        result.address_components?.find(
          component =>
            component.types.includes(
              "locality"
            )
        );

      if (cityComponent?.long_name) {
        localStorage.setItem(
          "vacancy_city",
          cityComponent.long_name
        );
      }

      saveCoordinates();

      localStorage.setItem(
        "vacancy_address",
        address.value.trim()
      );

      isMapLoading.value = true;
      return true;
    }

    latitude.value = null;
    longitude.value = null;
    saveCoordinates();

    addressError.value =
      data.status === "ZERO_RESULTS"
        ? "Не удалось найти такой адрес"
        : "Не удалось проверить адрес";

    return false;
  } catch (error) {
    if (error.name === "AbortError") {
      return false;
    }

    console.error(
      "❌ Ошибка геокодирования:",
      error
    );

    addressError.value =
      "Ошибка при поиске адреса";

    return false;
  } finally {
    if (sequence === geocodeSequence) {
      isSearchingAddress.value = false;
      emitValidationState();
    }
  }
}

async function saveAddressAndContinue() {
  isAddressTouched.value = true;

  if (!isAddressValid.value) {
    addressError.value =
      "Укажите адрес места работы";

    emit("notValid");
    return;
  }

  if (
    isManual.value &&
    !hasCoordinates.value
  ) {
    await geocodeManualAddress();
  }

  /*
    Настоящий адрес сохраняется здесь.
  */
  localStorage.setItem(
    "vacancy_address",
    address.value.trim()
  );

  localStorage.setItem(
    "vacancy_is_manual",
    String(isManual.value)
  );

  /*
    А ID ручного адреса сохраняется
    в отдельный ключ.
  */
  if (isManual.value) {
    const existingAddressID =
      localStorage.getItem(
        "vacancy_address_id"
      );

    localStorage.setItem(
      "vacancy_address_id",
      existingAddressID ||
      createManualAddressID()
    );
  } else {
    localStorage.removeItem(
      "vacancy_address_id"
    );
  }

  saveCoordinates();

  emit("complete-step");
}

watch(
  address,
  () => {
    if (isManual.value) {
      localStorage.setItem(
        "vacancy_address",
        address.value
      );
    }

    isMapLoading.value = true;
    emitValidationState();
  }
);

watch(
  mapUrl,
  () => {
    isMapLoading.value = true;
  }
);

onMounted(async () => {
  document.addEventListener(
    "save-address",
    saveAddressAndContinue
  );

  emitValidationState();

  await loadCompanyAddress();
});

onUnmounted(() => {
  document.removeEventListener(
    "save-address",
    saveAddressAndContinue
  );

  clearTimeout(geocodeTimer);
  geocodeController?.abort();
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-address-container {
  width: 100%;
  max-width: 760px;

  display: flex;
  flex-direction: column;

  margin-left: 40px;
  padding: 26px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 24px;

  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.02),
    0 12px 34px rgba(15, 23, 42, 0.04);

  animation: card-appearance 0.4s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 17px;
}

.step-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-heading-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.header-status,
.address-status {
  display: flex;
  align-items: center;
  gap: 5px;

  flex-shrink: 0;

  color: #28a46b;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  font-weight: 600;
}

.header-status svg,
.address-status svg {
  width: 15px;
  height: 15px;
}

.address-status.searching {
  color: #5686cc;
}

.small-loader {
  width: 13px;
  height: 13px;

  border: 2px solid rgba(67, 136, 245, 0.17);
  border-top-color: #4388f5;
  border-radius: 50%;

  animation: spin 0.7s linear infinite;
}

.step-description {
  max-width: 680px;
  margin: 0 0 20px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

/* Manual mode */

.manual-card {
  width: 100%;
  max-width: 680px;

  display: flex;
  align-items: center;
  gap: 11px;

  margin-bottom: 20px;
  padding: 13px 14px;

  cursor: pointer;

  background: #f8f9fb;

  border: 1px solid #e5e8ec;
  border-radius: 15px;

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.manual-card:hover {
  background: #ffffff;
  border-color: #d7dce3;

  transform: translateY(-1px);
}

.manual-card.active {
  background: rgba(54, 137, 246, 0.07);
  border-color: rgba(54, 137, 246, 0.2);

  box-shadow:
    0 7px 18px rgba(38, 100, 190, 0.07);
}

.manual-icon {
  width: 37px;
  height: 37px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #738093;
  background: #ffffff;

  border-radius: 11px;

  transition:
    color 0.22s ease,
    background-color 0.22s ease,
    transform 0.22s ease;
}

.manual-icon svg {
  width: 20px;
  height: 20px;
}

.manual-card.active .manual-icon {
  color: #ffffff;
  background: #4388f5;

  transform: rotate(-3deg) scale(1.03);
}

.manual-content {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.manual-title {
  color: #343b46;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.manual-description {
  color: #9299a5;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
  line-height: 1.4;
}

.toggle-input {
  position: absolute;

  width: 1px;
  height: 1px;

  opacity: 0;
  pointer-events: none;
}

.toggle-slider {
  position: relative;

  width: 46px;
  height: 27px;

  flex-shrink: 0;

  background: #d6dae0;
  border-radius: 999px;

  box-shadow:
    inset 0 1px 3px rgba(15, 23, 42, 0.11);

  transition:
    background-color 0.25s ease,
    box-shadow 0.25s ease;
}

.toggle-thumb {
  position: absolute;
  top: 4px;
  left: 4px;

  width: 19px;
  height: 19px;

  background: #ffffff;
  border-radius: 50%;

  box-shadow:
    0 2px 6px rgba(15, 23, 42, 0.19);

  transition:
    transform 0.25s
      cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toggle-input:checked + .toggle-slider {
  background:
    linear-gradient(
      135deg,
      #3095f8,
      #526ce9
    );
}

.toggle-input:checked
  + .toggle-slider
  .toggle-thumb {
  transform: translateX(19px);
}

/* Field */

.field-container {
  width: 100%;
  max-width: 680px;

  margin-bottom: 20px;
}

.field-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.field-heading > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-label {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.field-subtitle {
  color: #979eaa;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.address-input-shell {
  width: 100%;
  min-height: 54px;

  display: flex;
  align-items: center;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease,
    opacity 0.22s ease;
}

.address-input-shell.focused {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 24px rgba(31, 96, 205, 0.08);

  transform: translateY(-1px);
}

.address-input-shell.valid:not(.focused) {
  border-color: rgba(39, 162, 104, 0.36);
}

.address-input-shell.error {
  background: rgba(255, 246, 246, 0.9);
  border-color: #e15b5b;
}

.address-input-shell.disabled {
  opacity: 0.76;
}

.address-icon {
  width: 37px;
  height: 37px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 9px;

  color: #4388f5;
  background: rgba(67, 136, 245, 0.09);

  border-radius: 11px;
}

.address-icon svg {
  width: 20px;
  height: 20px;
}

.address-input {
  min-width: 0;
  width: 100%;
  height: 52px;

  padding: 0 12px;

  color: #252b35;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
}

.address-input::placeholder {
  color: #a5acb7;
}

.address-input:disabled {
  cursor: default;
}

.valid-icon {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 13px;

  color: #28a46b;
  background: rgba(40, 164, 107, 0.1);

  border-radius: 50%;
}

.valid-icon svg {
  width: 16px;
  height: 16px;
}

.field-message {
  min-height: 20px;

  display: flex;
  align-items: flex-start;
  gap: 7px;

  margin-top: 8px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1.45;
}

.field-message svg {
  width: 14px;
  height: 14px;

  flex-shrink: 0;
}

.hint-message {
  color: #8a93a2;
}

.error-message {
  color: #d84e4e;
  font-weight: 600;
}

/* Map */

.map-section {
  width: 100%;
  max-width: 680px;
}

.map-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;

  margin-bottom: 8px;
}

.map-heading > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.map-title {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.map-subtitle {
  color: #979eaa;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.coordinates {
  color: #74829a;
  background: #f1f4f8;

  padding: 5px 7px;

  border-radius: 7px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 7px;
}

.map-wrapper {
  position: relative;

  width: 100%;
  height: 260px;

  overflow: hidden;

  background: #e8ecf1;

  border: 1px solid #e1e5ea;
  border-radius: 17px;

  box-shadow:
    0 8px 22px rgba(15, 23, 42, 0.07);

  transform: translateZ(0);
}

.map-iframe {
  width: 100%;
  height: 100%;

  display: block;

  border: none;

  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}

.map-wrapper.loading .map-iframe {
  opacity: 0.35;
  transform: scale(1.015);
}

.map-loading {
  position: absolute;
  inset: 0;
  z-index: 4;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 10px;

  color: #778191;
  background: rgba(240, 243, 247, 0.78);
  backdrop-filter: blur(5px);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.map-loader-dots {
  display: flex;
  gap: 5px;
}

.map-loader-dots span {
  width: 6px;
  height: 6px;

  background: #4388f5;
  border-radius: 50%;

  animation: map-dot 1s ease-in-out infinite;
}

.map-loader-dots span:nth-child(2) {
  animation-delay: 0.13s;
}

.map-loader-dots span:nth-child(3) {
  animation-delay: 0.26s;
}

.map-gradient {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;

  height: 90px;

  pointer-events: none;

  background:
    linear-gradient(
      to top,
      rgba(20, 29, 44, 0.25),
      transparent
    );
}

.map-address-card {
  position: absolute;
  right: 11px;
  bottom: 11px;
  left: 11px;
  z-index: 3;

  display: flex;
  align-items: center;
  gap: 9px;

  padding: 10px 11px;

  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(15px);

  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 12px;

  box-shadow:
    0 7px 21px rgba(15, 23, 42, 0.13);
}

.map-pin {
  width: 32px;
  height: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #319aff,
      #4e6eef
    );

  border-radius: 10px;
}

.map-pin svg {
  width: 18px;
  height: 18px;
}

.map-address-content {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.map-address-content span {
  color: #8b93a1;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 7px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.map-address-content strong {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: #303641;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 9px;
}

/* Company error */

.company-error {
  width: 100%;
  max-width: 680px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  margin-top: 12px;
  padding: 10px 12px;

  color: #c84c51;
  background: rgba(207, 73, 79, 0.07);

  border: 1px solid rgba(207, 73, 79, 0.12);
  border-radius: 11px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.company-error button {
  flex-shrink: 0;

  padding: 6px 8px;

  cursor: pointer;

  color: #ffffff;
  background: #d45559;

  border: none;
  border-radius: 8px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 8px;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes map-dot {
  0%,
  60%,
  100% {
    opacity: 0.4;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

.status-enter-active,
.status-leave-active,
.message-enter-active,
.message-leave-active,
.map-loader-enter-active,
.map-loader-leave-active,
.map-card-enter-active,
.map-card-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.message-enter-from,
.message-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}

.map-loader-enter-from,
.map-loader-leave-to {
  opacity: 0;
}

.map-card-enter-from,
.map-card-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

/* Dark theme */

html[data-theme="dark"] .step-address-container {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .step-heading-title {
  color: #f1f2f4;
}

html[data-theme="dark"] .step-label {
  color: #777f8d;
}

html[data-theme="dark"] .step-description {
  color: #a5a9b2;
}

html[data-theme="dark"] .manual-card {
  background: #27272b;
  border-color: #393a40;
}

html[data-theme="dark"] .manual-card:hover {
  background: #2b2b30;
  border-color: #484950;
}

html[data-theme="dark"] .manual-card.active {
  background: rgba(54, 137, 246, 0.09);
  border-color: rgba(74, 145, 242, 0.2);
}

html[data-theme="dark"] .manual-icon {
  color: #969da8;
  background: #33343a;
}

html[data-theme="dark"] .manual-card.active .manual-icon {
  color: #ffffff;
  background: #4685df;
}

html[data-theme="dark"] .manual-title {
  color: #dcdfe4;
}

html[data-theme="dark"] .manual-description {
  color: #7b828e;
}

html[data-theme="dark"] .toggle-slider {
  background: #494a50;
}

html[data-theme="dark"] .field-label,
html[data-theme="dark"] .map-title {
  color: #d8dae0;
}

html[data-theme="dark"] .field-subtitle,
html[data-theme="dark"] .map-subtitle {
  color: #747b86;
}

html[data-theme="dark"] .address-input-shell {
  background: #28282c;
  border-color: #3b3b41;
}

html[data-theme="dark"] .address-input-shell.focused {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .address-input-shell.error {
  background: rgba(91, 37, 40, 0.2);
  border-color: #d85b60;
}

html[data-theme="dark"] .address-input {
  color: #f1f2f4;
  caret-color: #669cf5;
}

html[data-theme="dark"] .address-input::placeholder {
  color: #6f747e;
}

html[data-theme="dark"] .address-icon {
  color: #74a8f2;
  background: rgba(67, 136, 245, 0.1);
}

html[data-theme="dark"] .hint-message {
  color: #777d88;
}

html[data-theme="dark"] .coordinates {
  color: #93a2ba;
  background: #292b30;
}

html[data-theme="dark"] .map-wrapper {
  background: #27282c;
  border-color: #3a3b41;

  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .map-loading {
  color: #a0a6b0;
  background: rgba(35, 36, 41, 0.82);
}

html[data-theme="dark"] .map-address-card {
  background: rgba(37, 38, 43, 0.92);
  border-color: rgba(255, 255, 255, 0.08);

  box-shadow:
    0 9px 28px rgba(0, 0, 0, 0.28);
}

html[data-theme="dark"] .map-address-content strong {
  color: #f0f1f3;
}

/* Mobile */

@media (max-width: 600px) {
  .step-address-container {
    width: 100%;
    max-width: none;

    margin-left: 0;
    padding: 20px 16px;

    border-radius: 18px;
  }

  .step-icon {
    width: 39px;
    height: 39px;
    border-radius: 12px;
  }

  .step-heading-title {
    font-size: 16px;
  }

  .header-status {
    display: none;
  }

  .step-description {
    font-size: 12px;
    line-height: 1.55;
  }

  .manual-description {
    max-width: 190px;
  }

  .coordinates {
    display: none;
  }

  .address-input {
    font-size: 16px;
  }

  .map-wrapper {
    height: 225px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-address-container,
  .small-loader,
  .map-loader-dots span {
    animation: none;
  }

  .manual-card,
  .manual-icon,
  .toggle-slider,
  .toggle-thumb,
  .address-input-shell,
  .status-enter-active,
  .status-leave-active,
  .message-enter-active,
  .message-leave-active,
  .map-loader-enter-active,
  .map-loader-leave-active,
  .map-card-enter-active,
  .map-card-leave-active {
    transition: none;
  }
}
</style>